import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);

    console.error('[ErrorBoundary] caught:', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;

    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 p-8"
      >
        <h2 className="text-2xl font-semibold text-foreground">Something went wrong</h2>
        <p className="text-sm text-foreground-muted">
          An unexpected error occurred. Please try again.
        </p>
        {/* The raw error (which can carry internals/stack) is shown only in dev
            builds — never to real users — and is always in the console. */}
        {import.meta.env.DEV && (
          <pre className="max-w-full overflow-auto rounded bg-surface-muted p-2 text-xs text-danger">
            {error.message}
          </pre>
        )}
        <button
          type="button"
          onClick={this.reset}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand-hover"
        >
          Try again
        </button>
      </div>
    );
  }
}
